// All copy for the 2026 homepage lives here so wording can change without touching layout.
// Honesty rule: nothing on this page claims clients, results, or numbers we can't back up.
// The scorecard and "What an audit might find" are clearly labelled as samples/illustrations.

export const BOOKING_URL = 'https://calendly.com/primeslink/30min';

export const CONTACT = {
  email: 'info@primeslink.com',
  phone: '+1 914 525 5267',
  phoneHref: 'tel:+19145255267',
  addressLines: ['620 Mamaroneck Ave #24', 'White Plains, NY 10605-2081'],
  mapsHref: 'https://maps.google.com/?q=620+Mamaroneck+Ave+%2324,+White+Plains,+NY+10605',
};

export const SOCIALS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/primeslink-ai/' },
  { label: 'X', href: 'https://x.com/PrimeslinkHQ' },
  { label: 'YouTube', href: 'https://youtube.com/@PrimeslinkOfficial' },
];

export const NAV = [
  { label: 'How it works', href: '#how' },
  { label: 'The Audit', href: '#audit' },
  { label: "Who it's for", href: '#fit' },
  { label: 'FAQ', href: '#faq' },
];

export const CTA_LABEL = 'Book your free call';

// The six areas every audit scores. Used by the hero sample and the self-check.
export const AREAS = [
  'Lead response',
  'Follow-up',
  'Scheduling',
  'Reporting',
  'Data & tools',
  'Admin & documents',
];

export const SAMPLE_SCORECARD = {
  business: 'Sample · 22-person home services company',
  scores: [7, 3, 6, 4, 4, 5],
  leak: {
    title: 'Biggest leak: quote follow-up',
    detail: 'Quotes go out by email; nobody owns the follow-up.',
    est: '≈ 9 hrs / week',
  },
};

export const SYMPTOMS = [
  { quote: 'I’m the only one who knows how this works.', note: 'The business stalls whenever you step away.' },
  { quote: 'We type the same customer info into three different places.', note: 'Every re-type is a chance to get it wrong.' },
  { quote: 'Leads slip through the cracks when we’re busy.', note: 'The busiest weeks lose the most business.' },
  { quote: 'Sunday nights are for building reports.', note: 'The numbers exist — just not in one place.' },
  { quote: 'We tried an AI tool. Nobody uses it.', note: 'It didn’t fit how the work actually happens.' },
  { quote: 'I know AI could help. I just don’t know where to start.', note: 'Everyone has ideas. Nobody agrees on the first move.' },
];

export const STALL_POINTS = [
  {
    title: 'Scattered, messy data',
    body: 'AI is only as good as the information it can see. If a customer’s details live in five places, it will confidently use the wrong one.',
  },
  {
    title: 'Processes that live in one head',
    body: 'If the steps aren’t written down anywhere, there’s nothing for AI to follow — so it guesses, and people stop trusting it.',
  },
  {
    title: 'Tools that don’t talk to each other',
    body: 'Most small businesses run on a handful of apps that were never meant to work together. People become the glue.',
  },
];

export const AUDIT_GETS = [
  'A map of how work actually moves through your business — not how it looks on paper',
  'Where time and money quietly leak, in plain numbers of hours per week',
  'Every opportunity ranked by value and by effort',
  'A clear go / no-go on each — including where AI isn’t the answer',
  'A written 90-day roadmap',
];

export const AUDIT_FACTS = [
  { k: 'Cost', v: 'Free' },
  { k: 'Your time', v: 'About 2 hours, total' },
  { k: 'Format', v: 'One or two working sessions' },
  { k: 'Results', v: 'In writing, within 5 business days' },
  { k: 'Commitment', v: 'None — the plan is yours' },
];

export const STEPS = [
  {
    n: '01',
    title: 'A free 30-minute call',
    time: '30 minutes',
    body: 'A first conversation to see whether an audit is worth doing for your business.',
    gets: ['An honest first read', 'No slides, no pitch'],
  },
  {
    n: '02',
    title: 'The audit',
    time: '≈ 2 hours of your time',
    body: 'We map how a job moves through your business, from first contact to final invoice. We do the analysis; you just show up.',
    gets: ['Workflows and tools mapped', 'Leaks sized in hours per week'],
  },
  {
    n: '03',
    title: 'Your written plan',
    time: 'Within 5 business days',
    body: 'A scorecard across six areas, your biggest leaks, and a go / no-go on every opportunity.',
    gets: ['Scorecard + ranked opportunities', '90-day roadmap'],
  },
  {
    n: '04',
    title: 'Build only what’s worth it',
    time: 'Only if you want to',
    optional: true,
    body: 'Whatever the plan calls for, one fixed-scope project at a time. A person on your team approves anything that matters.',
    gets: ['Fixed scope, fixed price', 'Human approval built in'],
  },
  {
    n: '05',
    title: 'Hand it over properly',
    time: 'Ongoing, as needed',
    optional: true,
    body: 'Everything is documented, and your team can run it without us. We stay on for support only if that’s useful.',
    gets: ['Documentation + walkthrough', 'No lock-in'],
  },
];

// Illustrative only — composites of common small-business situations, NOT client results.
export const EXAMPLES = [
  {
    who: 'Home services · 20 people',
    heard: '“Quotes go out, then nothing happens.”',
    found: 'Quotes live in the owner’s inbox and nobody owns the follow-up, so most never get a second touch.',
    rec: 'A simple quote tracker plus automatic follow-up messages that the office manager reviews before they send.',
    verdict: 'go',
    verdictText: 'Go — AI drafts, a person approves',
  },
  {
    who: 'Accounting firm · 12 people',
    heard: '“Monday reporting takes half a day.”',
    found: 'The same figures are copied by hand out of three systems into one spreadsheet every week.',
    rec: 'Connect the three systems and build the report automatically. Mostly plumbing — very little AI needed.',
    verdict: 'go',
    verdictText: 'Go — automation, not AI',
  },
  {
    who: 'Property management · 9 people',
    heard: '“After-hours calls go to voicemail.”',
    found: 'Most after-hours calls turn out to be the same handful of routine questions; true emergencies are rare.',
    rec: 'An AI phone assistant for routine questions and maintenance requests, with real emergencies routed to the on-call manager.',
    verdict: 'go',
    verdictText: 'Go — voice assistant with a human fallback',
  },
  {
    who: 'Retail boutique · 6 people',
    heard: '“We want an AI chatbot on our website.”',
    found: 'The site gets few questions. The real drain is re-typing inventory between the register and the online store.',
    rec: 'Skip the chatbot for now. Sync the register with the online store instead.',
    verdict: 'no',
    verdictText: 'No-go on the chatbot — not worth it yet',
  },
];

// Kept short on purpose: the pain points themselves live in SYMPTOMS ("Sound familiar?").
export const FIT_FOR = [
  'You run a business of roughly 5 to 100 people.',
  'Two or three of the frustrations above sound like your week.',
  'The same kinds of tasks come back every single week.',
  'You want a straight answer, not a sales pitch.',
];

export const FIT_NOT = [
  'You want AI because it’s trendy, not because something’s broken.',
  'You’re after a 100-page strategy deck.',
  'You’ve already picked a tool and just want someone to install it.',
  'You’re not ready to change how any of the work gets done.',
];

export const FAQS = [
  {
    q: 'Is the audit really free?',
    a: 'Yes. If the plan shows something worth building, we’ll quote it as a fixed-scope project, and you decide then.',
  },
  {
    q: 'Do I have to hire you afterward?',
    a: 'No. The written plan is yours either way. Implement it in-house, with another provider, or with us.',
  },
  {
    q: 'What if AI isn’t the right answer for my business?',
    a: 'Then we’ll tell you. Plenty of fixes are a process change or connecting two tools you already pay for — no AI required. The go / no-go on each opportunity is there for exactly this reason.',
  },
  {
    q: 'I’m not technical. Is that a problem?',
    a: 'Not at all — that’s who this is for. Everything comes with a plain-English explanation of what it does, why it matters, and what acting on it would involve.',
  },
  {
    q: 'Do you need access to my systems or data?',
    a: 'Not for the audit. We work from conversations and screen-shares of how you do things today. If you build with us later, access is limited to what that project needs, and a person on your team approves anything that goes out.',
  },
  {
    q: 'What kinds of things do you build?',
    a: 'Whatever the plan calls for: automated follow-ups, connected tools, self-updating dashboards and reports, AI assistants that draft work for your team to approve, and AI phone assistants. We build one piece at a time, only where it pays for itself.',
  },
];

export const SELF_CHECK = [
  {
    area: 'Lead response',
    q: 'When a new inquiry comes in, how quickly does someone reply?',
    options: [
      { label: 'Within the hour, every time', score: 9 },
      { label: 'Usually the same day', score: 6 },
      { label: 'Whenever someone gets to it', score: 3 },
    ],
  },
  {
    area: 'Follow-up',
    q: 'After you send a quote or proposal, what happens next?',
    options: [
      { label: 'Follow-ups go out automatically', score: 9 },
      { label: 'Someone usually remembers', score: 6 },
      { label: 'It often just goes quiet', score: 3 },
    ],
  },
  {
    area: 'Scheduling',
    q: 'How do appointments or jobs get booked?',
    options: [
      { label: 'Customers book online; it syncs', score: 9 },
      { label: 'A mix of phone, email, and a calendar', score: 6 },
      { label: 'Lots of back-and-forth calls and texts', score: 3 },
    ],
  },
  {
    area: 'Reporting',
    q: 'How do you see how last week went?',
    options: [
      { label: 'A dashboard that updates itself', score: 9 },
      { label: 'Someone builds a report by hand', score: 6 },
      { label: 'We don’t really — or it takes hours', score: 3 },
    ],
  },
  {
    area: 'Data & tools',
    q: 'How many places does the same customer information get typed into?',
    options: [
      { label: 'Just one', score: 9 },
      { label: 'Two or three', score: 6 },
      { label: 'Four or more', score: 3 },
    ],
  },
  {
    area: 'Admin & documents',
    q: 'How much of your team’s week goes to forms, invoices, and data entry?',
    options: [
      { label: 'Very little', score: 9 },
      { label: 'A few hours a week', score: 6 },
      { label: 'It’s a big part of someone’s job', score: 3 },
    ],
  },
];
